import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AppProvider } from '@/contexts/AppContext';
import { Toaster } from '@/components/ui/sonner';
import { playWelcomeChime, playNotificationChime } from '@/utils/sounds';

// Layouts
import MainLayout from '@/components/layout/MainLayout';

// Pages - Protected
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import SuppliersPage from '@/pages/SuppliersPage';
import SupplierProfilePage from '@/pages/SupplierProfilePage';
import NewCasePage from '@/pages/NewCasePage';
import VisaCenterPage from '@/pages/VisaCenterPage';
import SupplierReportsPage from '@/pages/SupplierReportsPage';
import FlightScannerPage from '@/pages/FlightScannerPage';
import TransactionsPage from '@/pages/TransactionsPage';
import ArrivalsMapPage from '@/pages/ArrivalsMapPage';
import ExportSystemPage from '@/pages/ExportSystemPage';
import OverseasWorkersPage from '@/pages/OverseasWorkersPage';
import SettingsPage from '@/pages/SettingsPage';
import AdvancePaymentsPage from '@/pages/AdvancePaymentsPage';
import WorkerTreePage from '@/pages/WorkerTreePage';

// Pages - Public (No auth required)
import PassportTrackerPage from '@/pages/PassportTrackerPage';
import VisaTrackerPage from '@/pages/VisaTrackerPage';
import PublicTrackerPage from '@/pages/PublicTrackerPage';
import VisaEmbedPage from '@/pages/VisaEmbedPage';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A2D47] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#D35400] to-[#A04000] flex items-center justify-center animate-pulse">
            <span className="text-3xl font-bold text-white">T</span>
          </div>
          <p className="text-white/60">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route - redirect to dashboard if logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A2D47] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#D35400] to-[#A04000] flex items-center justify-center animate-pulse">
            <span className="text-3xl font-bold text-white">T</span>
          </div>
          <p className="text-white/60">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* ========== PUBLIC ROUTES (No Auth Required) ========== */}
      
      {/* Public Tracker Pages - These are accessible without login */}
      <Route path="/passport-tracker" element={<PassportTrackerPage />} />
      <Route path="/visa-tracker" element={<VisaTrackerPage />} />
      <Route path="/visa-embed" element={<VisaEmbedPage />} />
      <Route path="/tracker" element={<PublicTrackerPage />} />
      
      {/* Login Page */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* ========== PROTECTED ROUTES (Auth Required) ========== */}
      <Route
        element={
          <ProtectedRoute>
            <AppProvider>
              <MainLayout />
            </AppProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/suppliers/:id" element={<SupplierProfilePage />} />
        <Route path="/new-case" element={<NewCasePage />} />
        <Route path="/visa-center" element={<VisaCenterPage />} />
        <Route path="/flight-scanner" element={<FlightScannerPage />} />
        <Route path="/arrivals-map" element={<ArrivalsMapPage />} />
        <Route path="/supplier-reports" element={<SupplierReportsPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />
        <Route path="/export-system" element={<ExportSystemPage />} />
        <Route path="/overseas-workers" element={<OverseasWorkersPage />} />
        <Route path="/advance-payments" element={<AdvancePaymentsPage />} />
        <Route path="/worker-tree" element={<WorkerTreePage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  // Autoplay policy: browsers require user gesture before audio plays. So we
  // register listeners for the FIRST user interaction (click/touch/key) and
  // then fire the welcome chime once (respecting the Settings preference).
  useEffect(() => {
    const trigger = () => {
      playWelcomeChime();
      window.removeEventListener('click', trigger);
      window.removeEventListener('touchstart', trigger);
      window.removeEventListener('keydown', trigger);
    };
    window.addEventListener('click', trigger, { once: false });
    window.addEventListener('touchstart', trigger, { once: false });
    window.addEventListener('keydown', trigger, { once: false });
    return () => {
      window.removeEventListener('click', trigger);
      window.removeEventListener('touchstart', trigger);
      window.removeEventListener('keydown', trigger);
    };
  }, []);

  // Hook sonner toasts → notification chime (if enabled in settings).
  // Uses a MutationObserver on document.body so it catches any [data-sonner-toaster]
  // element and then watches its children for new toast nodes.
  useEffect(() => {
    let toastObserver = null;
    let containerObserver = null;

    const attachToastObserver = (container) => {
      if (toastObserver) return;
      toastObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.addedNodes.length > 0) {
            // Only play once per batch even if several toasts come at once
            playNotificationChime();
            break;
          }
        }
      });
      toastObserver.observe(container, { childList: true });
    };

    // Look for an existing Sonner container, or wait for it to mount
    const existing = document.querySelector('[data-sonner-toaster]');
    if (existing) {
      attachToastObserver(existing);
    } else {
      containerObserver = new MutationObserver(() => {
        const c = document.querySelector('[data-sonner-toaster]');
        if (c) {
          attachToastObserver(c);
          containerObserver.disconnect();
          containerObserver = null;
        }
      });
      containerObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      if (toastObserver) toastObserver.disconnect();
      if (containerObserver) containerObserver.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#243B5C',
              color: 'white',
              border: '1px solid rgba(0, 75, 147, 0.3)',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
